import { Dispatch } from "redux";
import { IStore } from "reducers";
import _ from "lodash";
import {
    IFeedsFilters,
    INotificationFilters,
    INotificationStatusParams,
    INotification,
    IExportNotificationStatusParams,
} from "reducers/notifications";
import { NOTIFICATIONS, WHITEBOARD_EXPORT_NOTIFICATIONS } from "constants/action-types";
import { Slug } from "constants/notifications";

export const PAGE_SIZE = 20;

export const startPolling = (payload?: Slug[]) => ({ type: NOTIFICATIONS.POLL.START, payload });

export const stopPolling = () => ({ type: NOTIFICATIONS.POLL.STOP });

// prettier-ignore
export const fetchFeeds = (filters?: IFeedsFilters, next?: boolean) =>
    (dispatch: Dispatch<{}>, getStore: () => IStore) => {
        const list = getStore().notifications.feeds.payload;
        const params = { limit: PAGE_SIZE, ...filters };

        if (next && !_.isEmpty(list)) {
            params.start_id_open = _.last(list).id;
        }
        return dispatch({
            payload: params,
            type: NOTIFICATIONS.FEEDS.FETCH.START,
        });
    };

// prettier-ignore
export const fetchNotifications = (slug: Slug, filters: INotificationFilters = {}, next?: boolean) =>
    (dispatch: Dispatch<{}>, getStore: () => IStore) => {
        const list = getStore().notifications.feedNotifications.payload?.[slug]?.list;
        const params = { limit: PAGE_SIZE, ...filters };

        if (next && !_.isEmpty(list)) {
            params.start_id_open = _.last(list).id;
        }
        return dispatch({
            payload: { slug, params },
            type: NOTIFICATIONS.FEED_NOTIFICATIONS.FETCH.START,
        });
    };

// prettier-ignore
export const updateNotificationStatus = (slug: Slug, { seen, read, readAll }: INotificationStatusParams) =>
    (dispatch: Dispatch<{}>, getStore: () => IStore) => {
        const { list, hasNextPage } = getStore().notifications.feedNotifications.payload?.[slug] || {};

        const update = readAll ? _.map(list, item => ({ ...item, is_read: true, is_seen: true })) : [...list];
        if (!readAll) {
            _.forEach(read, (index: number) => update[index] && _.set(update, index, { ...update[index], is_read: true, is_seen: true }));
            _.forEach(seen, (index: number) => update[index] && _.set(update, index, { ...update[index], is_seen: true }));
        }

        dispatch({
            type: NOTIFICATIONS.FEED_NOTIFICATIONS.UPDATE,
            payload: {
                ...getStore().notifications.feedNotifications.payload,
                [slug]: {
                    list: update,
                    hasNextPage,
                },
            },
        });

        dispatch({
            type: NOTIFICATIONS.FEEDS.UPDATE,
            payload: _.map(getStore().notifications.feeds.payload,
                feed => feed.feed_slug_id !== slug
                    ? feed
                    : {
                        ...feed,
                        unseen: readAll ? 0 : _.toString(Math.max(Number(feed.unseen) - _.size(seen), 0)),
                        unread: readAll ? 0 : _.toString(Math.max(Number(feed.unread) - _.size(read), 0)),
                    },
            ),
        });

        return dispatch({
            type: NOTIFICATIONS.FEED_NOTIFICATIONS.STATUS,
            payload: {
                slug,
                params: {
                    mark_seen_notification_ids: _.map(seen, index => _.get(list, [index, "id"])),
                    mark_read_notification_ids: !readAll ? _.map(read, index => _.get(list, [index, "id"])) : [],
                    mark_read_all: readAll,
                    mark_seen_all: readAll,
                },
            },
        });
    };

export const updateExportNotificationStatus =
    ({ seen, read, export_id, readAll }: IExportNotificationStatusParams) =>
    (dispatch: Dispatch<{}>, getStore: () => IStore) => {
        const exportNotifications = getStore().exportNotifications;
        const { exports } = exportNotifications || {};

        const update = readAll ? _.map(exports, (item) => ({ ...item, is_read: true, is_seen: true })) : [...exports];

        if (!readAll) {
            exports.forEach((element) => {
                if (!element.is_seen && seen) {
                    _.set(_.find(update, { id: element.id }), "is_seen", true);
                }
            });
            if (export_id) {
                _.set(_.find(update, { id: export_id }), "is_read", true);
                _.set(_.find(update, { id: export_id }), "is_seen", true);
            }
        }

        dispatch({
            type: WHITEBOARD_EXPORT_NOTIFICATIONS.UPDATE,
            payload: {
                ...exportNotifications,
                exports: update,
            },
        });

        dispatch({
            type: WHITEBOARD_EXPORT_NOTIFICATIONS.STATUS,
            payload: {
                ...exportNotifications,
                unseen: readAll ? 0 : exportNotifications.unseen - (seen ? seen : 0),
                unread: readAll ? 0 : exportNotifications.unread - (read ? read : 0),
            },
        });
    };

export const loadPolledNotifications = (slug: Slug, newEntries: INotification[]) => ({
    type: NOTIFICATIONS.FEED_NOTIFICATIONS.UPDATE,
    payload: {
        [slug]: {
            list: newEntries,
            hasNextPage: _.size(newEntries) >= PAGE_SIZE,
        },
    },
});
